import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services';
import { useAuthStore } from '../../stores/authStore';
import config from '../../config';
import PhoneField from './components/PhoneField';
import ErrorAlert from './components/ErrorAlert';
import SubmitButton from './components/SubmitButton';

import AuthBackground from './components/AuthBackground';
import { startSmsOtp, cancelSmsOtp } from './webOtp';

const Login: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const normalizePhone = (raw: string) => {
    const cleaned = raw.replace(/[\s\-()]/g, '');
    if (/^\+98\d{10}$/.test(cleaned)) {
      return `0${cleaned.slice(3)}`;
    }
    if (/^09\d{9}$/.test(cleaned)) {
      return cleaned;
    }
    return null;
  };

  const formatDisplayPhone = (value: string) => {
    const cleaned = value.replace(/[^0-9+]/g, '');
    if (cleaned.startsWith('+98')) {
      const local = cleaned.slice(3);
      const g = local.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (g) return `+98 ${g[1]} ${g[2]} ${g[3]}`;
      return `+98 ${local}`;
    }
    const digits = cleaned.replace(/\D/g, '');
    if (digits.length <= 4) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d+ ]/g, '');
    setPhoneNumber(raw);
    setError('');
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const normalized = normalizePhone(phoneNumber);
    if (!normalized) {
      setError('شماره موبایل باید در قالب 0912... یا +98 912... باشد');
      return;
    }

    setLoading(true);

    try {
      void startSmsOtp(normalized);
      await authService.sendOTP(normalized);
      navigate('/otp', { state: { phoneNumber: normalized } });
    } catch (err: any) {
      cancelSmsOtp();
      console.error('Send OTP error:', err);
      const errorMessage =
        err.response?.data?.error?.message_fa ||
        err.response?.data?.error?.message ||
        'خطا در ارسال کد تایید';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isValidPhone = !!normalizePhone(phoneNumber);
  const logoSrc = `${config.app.baseUrl}assets/images/raaz-logo.png`;

  return (
    <main
      role="main"
      aria-labelledby="login-title"
      className="min-h-screen w-full flex items-center justify-center p-4 sm:p-6 md:p-8 lg:p-10 relative overflow-x-hidden overflow-y-auto"
    >
      <AuthBackground />
      
      {/* Content Container */}

      <div className="w-full max-w-md px-0 py-5 sm:p-8 relative z-10">
        {/* Card */}
        <div
          className="rounded-3xl shadow-2xl p-5 min-[360px]:p-6 sm:p-8 md:p-10"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            animation: 'fadeIn 0.5s ease-out',
          }}
        >
          <a
            href="/"
            className="mb-6 inline-flex min-h-11 items-center gap-2 rounded-xl px-3 text-sm font-bold text-[#0e444e] transition-colors hover:bg-[#0e444e]/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#b58a49]"
            aria-label="بازگشت به وب‌سایت راز"
          >
            <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5m6-6-6 6 6 6" />
            </svg>
            بازگشت به وب‌سایت
          </a>
          <div className="flex flex-col items-center mb-8 sm:mb-12">
            <div className="w-20 h-20 mb-6 relative">
              <div className="absolute inset-0 bg-[#d5b481]/20 rounded-2xl rotate-3"></div>
              <div className="relative w-full h-full bg-white rounded-2xl shadow-md flex items-center justify-center p-3 border border-[#d5b481]/30">
                <img src={logoSrc} alt="طلا و جواهر راز" className="w-full h-full object-contain" />
              </div>
            </div>
            <h2 id="login-title" className="text-2xl font-black text-[#0e444e]">ورود به پنل</h2>
          </div>

          {/* Form */}
          <form onSubmit={handleSendOTP} className="space-y-6 sm:space-y-8" noValidate>
            <p id="phone-description" className="sr-only">
              شماره موبایل باید در قالب 0912 345 6789 یا +98 912 345 6789 باشد
            </p>
            <PhoneField
              value={formatDisplayPhone(phoneNumber)}
              onChange={handlePhoneChange}
              loading={loading}
              describedBy={error ? 'phone-error' : 'phone-description'}
              invalid={!!error}
            />

            {/* Error Message */}
            <ErrorAlert text={error} id="phone-error" />

            {/* Submit Button */}
            <SubmitButton loading={loading} disabled={!isValidPhone}>
              دریافت کد تایید
            </SubmitButton>
          </form>

          {/* Footer */}
          <div className="mt-8 sm:mt-10 pt-6 sm:pt-8" style={{ borderTop: '1px solid #e5e7eb' }}>
            <p className="text-xs sm:text-sm text-center leading-relaxed" style={{ color: '#6b7280' }}>
              با ورود به اپلیکیشن،{' '}
              <a
                href="/terms"
                className="font-bold underline-offset-4 hover:underline transition-colors duration-200"
                style={{ color: '#d5b481' }}
              >
                قوانین و مقررات
              </a>{' '}
              را می‌پذیرید.
            </p>
          </div>
        </div>

        {/* Version Badge */}
        <p
          className="text-center mt-6 sm:mt-8 text-xs sm:text-sm font-medium"
          style={{ color: 'rgba(255, 255, 255, 0.7)' }}
        >
          نسخه ۱.۰.۰
        </p>
      </div>
    </main>
  );
};

export default Login;
